/**
 * CxNotificationSection - Configure CX notification settings and remedies
 *
 * Settings: sender email, reply-to email, enabled toggle
 * Remedies: configurable list (label + template paragraph) following redo reasons pattern
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2,
  Save,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  RotateCcw,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

// ============================================================================
// Settings Section
// ============================================================================

export function CxNotificationSettingsSection() {
  const { t } = useTranslation('settings');
  const [enabled, setEnabled] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const settingsQuery = trpc.productionSettings.getCxNotificationSettings.useQuery();

  const updateMutation = trpc.productionSettings.updateCxNotificationSettings.useMutation({
    onSuccess: () => {
      toast.success(t('production.cxNotification.saved'));
      setHasChanges(false);
      settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setEnabled(settingsQuery.data.settings.enabled);
      setSenderEmail(settingsQuery.data.settings.senderEmail);
      setReplyToEmail(settingsQuery.data.settings.replyToEmail);
      setHasChanges(false);
    }
  }, [settingsQuery.data]);

  const handleSave = () => {
    updateMutation.mutate({ enabled, senderEmail, replyToEmail });
  };

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Send email notifications to customers when their order is affected by an out-of-stock issue.
      </p>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label htmlFor="cx-enabled" className="font-medium">Enable CX Notifications</Label>
          <p className="text-sm text-muted-foreground">Allow sending email notifications from the Issues screen</p>
        </div>
        <Switch
          id="cx-enabled"
          checked={enabled}
          onCheckedChange={(v) => { setEnabled(v); setHasChanges(true); }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="cx-sender" className="text-sm text-muted-foreground">Sender Email</Label>
          <Input
            id="cx-sender"
            type="email"
            value={senderEmail}
            onChange={(e) => { setSenderEmail(e.target.value); setHasChanges(true); }}
            placeholder="orders@yourstore.com"
            className="h-11"
          />
          <p className="text-xs text-muted-foreground">Must be verified in Resend</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="cx-replyto" className="text-sm text-muted-foreground">Reply-To Email</Label>
          <Input
            id="cx-replyto"
            type="email"
            value={replyToEmail}
            onChange={(e) => { setReplyToEmail(e.target.value); setHasChanges(true); }}
            placeholder="hello@yourstore.com"
            className="h-11"
          />
          <p className="text-xs text-muted-foreground">Customer replies go here (e.g. helpdesk)</p>
        </div>
      </div>

      {hasChanges && (
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="h-12 gap-2"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Settings
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Remedies Section
// ============================================================================

interface CxRemedy {
  value: string;
  label: string;
  template: string;
}

const DEFAULT_CX_REMEDIES: CxRemedy[] = [
  {
    value: 'restocking',
    label: "We're restocking",
    template: "We're sourcing this item and expect to ship within a few business days. We'll keep you updated.",
  },
  {
    value: 'pick_different',
    label: 'Pick a different item',
    template: "Would you like to choose a substitute? Reply to this email with your preference and we'll sort it out.",
  },
  {
    value: 'remove_and_refund',
    label: 'Remove item + refund',
    template: "We'll remove this item from your order and issue a refund for it. The rest of your order will ship as normal.",
  },
  {
    value: 'cancel_full_refund',
    label: 'Cancel order + full refund',
    template: "We've cancelled your order and issued a full refund. You should see it back in your account within 5-10 business days.",
  },
];

function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function CxRemediesSection() {
  const { t } = useTranslation('settings');
  const [remedies, setRemedies] = useState<CxRemedy[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  // New remedy form state
  const [newLabel, setNewLabel] = useState('');
  const [newTemplate, setNewTemplate] = useState('');

  const remediesQuery = trpc.productionSettings.getCxRemedies.useQuery();

  const updateMutation = trpc.productionSettings.updateCxRemedies.useMutation({
    onSuccess: () => {
      toast.success(t('production.cxNotification.remediesSaved'));
      setHasChanges(false);
      remediesQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  useEffect(() => {
    if (remediesQuery.data) {
      setRemedies(remediesQuery.data.remedies);
      setHasChanges(false);
    }
  }, [remediesQuery.data]);

  const handleAdd = () => {
    const label = newLabel.trim();
    const template = newTemplate.trim();
    if (!label || !template) return;

    const value = toSnakeCase(label);
    if (!value) {
      toast.error('Label must contain at least one letter or number');
      return;
    }

    if (remedies.some(r => r.value === value)) {
      toast.error(`A remedy with value "${value}" already exists`);
      return;
    }

    if (remedies.length >= 10) {
      toast.error('Maximum of 10 remedies allowed');
      return;
    }

    setRemedies(prev => [...prev, { value, label, template }]);
    setNewLabel('');
    setNewTemplate('');
    setHasChanges(true);
  };

  const handleRemove = (value: string) => {
    if (remedies.length <= 1) {
      toast.error('At least one remedy is required');
      return;
    }
    setRemedies(prev => prev.filter(r => r.value !== value));
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setRemedies(prev => {
      const next = [...prev];
      const temp = next[index]!;
      next[index] = next[index - 1]!;
      next[index - 1] = temp;
      return next;
    });
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === remedies.length - 1) return;
    setRemedies(prev => {
      const next = [...prev];
      const temp = next[index]!;
      next[index] = next[index + 1]!;
      next[index + 1] = temp;
      return next;
    });
    setHasChanges(true);
  };

  const handleEditTemplate = (index: number, newTemplate: string) => {
    setRemedies(prev => {
      const next = [...prev];
      next[index] = { ...next[index]!, template: newTemplate };
      return next;
    });
    setHasChanges(true);
  };

  const handleResetToDefaults = () => {
    setRemedies(DEFAULT_CX_REMEDIES);
    setEditingIdx(null);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ remedies });
  };

  if (remediesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure the remedy options available when notifying customers about OOS items.
        Each remedy includes a message template sent in the email.
      </p>

      {/* Existing remedies */}
      {remedies.length > 0 && (
        <div className="space-y-3">
          {remedies.map((remedy, index) => (
            <div key={remedy.value} className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">{remedy.label}</span>
                  <span className="text-xs text-muted-foreground ml-2 font-mono">{remedy.value}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    aria-label={`Move ${remedy.label} up`}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === remedies.length - 1}
                    aria-label={`Move ${remedy.label} down`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => handleRemove(remedy.value)}
                    aria-label={`Remove ${remedy.label}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {editingIdx === index ? (
                <div className="space-y-2">
                  <Textarea
                    value={remedy.template}
                    onChange={(e) => handleEditTemplate(index, e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingIdx(null)}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <p
                  className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => setEditingIdx(index)}
                  title="Click to edit template"
                >
                  {remedy.template}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new remedy form */}
      <div className="space-y-2 rounded-lg border border-dashed p-4">
        <Label className="text-sm font-medium">Add New Remedy</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="new-remedy-label" className="text-xs text-muted-foreground">
              Label
            </Label>
            <Input
              id="new-remedy-label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Store credit"
              className="h-11"
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="new-remedy-template" className="text-xs text-muted-foreground">
            Email Template Text
          </Label>
          <Textarea
            id="new-remedy-template"
            value={newTemplate}
            onChange={(e) => setNewTemplate(e.target.value)}
            placeholder="The message that will be included in the customer email..."
            rows={2}
            maxLength={500}
          />
        </div>
        <Button
          variant="outline"
          className="h-11"
          onClick={handleAdd}
          disabled={!newLabel.trim() || !newTemplate.trim()}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Remedy
        </Button>
      </div>

      {/* Empty state */}
      {remedies.length === 0 && (
        <p className="text-sm text-muted-foreground/70 text-center py-2">
          No remedies configured. Add at least one remedy option.
        </p>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResetToDefaults}
          className="text-muted-foreground"
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          {t('production.cxNotification.resetToDefaults')}
        </Button>

        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending || remedies.length === 0}
            className="h-12 gap-2"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t('production.common.saveChanges')}
          </Button>
        )}
      </div>
    </div>
  );
}
